import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';

export const UK_REGION = 'uk';
export const US_REGION = 'us';

// The business is UK-based and all Prismic copy is British English, so UK is both the fallback
// and what the statically generated HTML contains.
export const DEFAULT_REGION = UK_REGION;

export const REGION_COOKIE = 'ck_region';

const REGIONS = [UK_REGION, US_REGION];
const COOKIE_EXPIRY_DAYS = 30;

/**
 * Every IANA time zone that means the United States, including the deprecated US/* aliases that
 * some browsers still report. Anything not in this list falls through to the UK default, which is
 * what the old country lookup did too: only GB and US ever produced a region, everything else got
 * the default.
 */
const US_TIME_ZONES = new Set([
  'America/Adak',
  'America/Anchorage',
  'America/Boise',
  'America/Chicago',
  'America/Denver',
  'America/Detroit',
  'America/Indiana/Indianapolis',
  'America/Indiana/Knox',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Tell_City',
  'America/Indiana/Vevay',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Juneau',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Los_Angeles',
  'America/Menominee',
  'America/Metlakatla',
  'America/New_York',
  'America/Nome',
  'America/North_Dakota/Beulah',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/Phoenix',
  'America/Sitka',
  'America/Yakutat',
  'Pacific/Honolulu',
  'US/Alaska',
  'US/Aleutian',
  'US/Arizona',
  'US/Central',
  'US/East-Indiana',
  'US/Eastern',
  'US/Hawaii',
  'US/Indiana-Starke',
  'US/Michigan',
  'US/Mountain',
  'US/Pacific',
]);

/**
 * The region as a plain module value, mirroring the state below.
 *
 * Prismic text is rendered by the plain functions in src/utils/prismic.js, which are called
 * conditionally all over the component tree, so they cannot read context (a hook there would
 * break the rules of hooks). Reading it from here instead keeps the spelling transform out of
 * every call site; RegionProvider re-mounts the tree when the value changes so nothing renders
 * stale text. Only ever written in the browser, so server-side rendering always sees the default.
 */
let activeRegion = DEFAULT_REGION;

export const getActiveRegion = () => activeRegion;

const RegionContext = createContext(DEFAULT_REGION);

// Kept separate from the region itself so that reading the region and being able to change it
// are independent: `useRegion` consumers re-render on every change, the toggle never needs to.
const SelectRegionContext = createContext(() => {});

export const useRegion = () => useContext(RegionContext);

/**
 * Returns a function that switches the site to a region because the visitor asked for it,
 * as opposed to because detection inferred it. See RegionToggle.
 */
export const useSelectRegion = () => useContext(SelectRegionContext);

const timeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (error) {
    return '';
  }
};

const languageTags = () => {
  if (typeof navigator === 'undefined') return [];

  const tags = Array.isArray(navigator.languages) && navigator.languages.length
    ? navigator.languages
    : [navigator.language];

  return tags.filter(Boolean).map(tag => String(tag).toLowerCase());
};

/**
 * Works out the region from what the browser already knows about itself, with no network call and
 * so nothing disclosed to anyone. The time zone is the stronger signal and is checked first; the
 * language tag only decides the case where the time zone is missing or unrecognised, which is what
 * a privacy-hardened browser reporting UTC looks like.
 *
 * Returns null when neither signal says anything, so the caller leaves the cookie unwritten and
 * the next page load can decide again rather than pinning the visitor to the default for a month.
 */
const detectRegion = () => {
  const zone = timeZone();
  if (US_TIME_ZONES.has(zone)) return US_REGION;
  if (zone === 'Europe/London' || zone === 'GB' || zone === 'GB-Eire') return UK_REGION;

  const tags = languageTags();
  if (tags.some(tag => tag === 'en-us' || tag.startsWith('en-us-'))) return US_REGION;
  if (tags.some(tag => tag === 'en-gb' || tag.startsWith('en-gb-'))) return UK_REGION;

  // A known zone outside both regions, such as Europe/Berlin or America/Toronto, is a positive
  // answer: the visitor is in neither, so the UK default is the right one and worth remembering.
  if (zone) return DEFAULT_REGION;

  return null;
};

const rememberRegion = region => Cookies.set(REGION_COOKIE, region, {
  expires: COOKIE_EXPIRY_DAYS,
  path: '/',
  sameSite: 'lax',
});

export function RegionProvider({ children }) {
  const [region, setRegion] = useState(DEFAULT_REGION);

  const applyRegion = useCallback(next => {
    activeRegion = next;
    setRegion(next);
  }, []);

  const selectRegion = useCallback(next => {
    if (!REGIONS.includes(next)) return;

    rememberRegion(next);
    applyRegion(next);
  }, [applyRegion]);

  useEffect(() => {
    // The cookie is read before detection ever runs, and it is written by both the toggle and
    // detection, so on every later page load a manual choice wins: there is no path from here to
    // a detection result that could overwrite it.
    const cached = Cookies.get(REGION_COOKIE);
    if (REGIONS.includes(cached)) {
      applyRegion(cached);

      return;
    }

    // Synchronous, so it resolves in this same effect and cannot land after the visitor has used
    // the toggle. That is what the in-flight guard used to be for.
    const detected = detectRegion();
    if (!detected) return;

    rememberRegion(detected);
    applyRegion(detected);
  }, [applyRegion]);

  // Detection can only run after hydration, so the first paint has to be the UK default that
  // matches the server-rendered HTML. The key re-mounts the page once the region changes, which
  // is what pushes the new spellings through the components that read getActiveRegion(). The
  // toggle goes through the same path, so switching by hand updates prices and spellings
  // without a page load.
  return (
    <RegionContext.Provider value={region}>
      <SelectRegionContext.Provider value={selectRegion}>
        <React.Fragment key={region}>{children}</React.Fragment>
      </SelectRegionContext.Provider>
    </RegionContext.Provider>
  );
}

RegionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const wrapRootElement = ({ element }) => <RegionProvider>{element}</RegionProvider>;
